import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom"; // ✅ إضافة useNavigate
import { apiUrl } from "../../../apiConfig";
import { useAdminAuth } from "../../../hooks/useAdminAuth"; // ✅ استيراد الـ Hook
import "./AdminApplicants.css";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_LABELS = {
  pending: "Pending",
  reviewed: "Reviewed",
  accepted: "Accepted",
  rejected: "Rejected",
};

function getToken() {
  return (
    localStorage.getItem("ihcc-admin-token") ||
    sessionStorage.getItem("ihcc-admin-token")
  );
}

function authHeaders() {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
    headers["X-Admin-Token"] = token;
  }
  return headers;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function StatusBadge({ status }) {
  const key = STATUS_LABELS[status] ? status : "pending";
  return (
    <span className={`status-badge status-badge--${key}`}>
      {STATUS_LABELS[key]}
    </span>
  );
}

function ApplicantModal({ applicant, onClose, onStatusChange }) {
  const [isDownloading, setIsDownloading] = useState(false);
  
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const token = getToken();
  const resumeUrl = applicant.id
    ? apiUrl(`/applications.php?download=resume&id=${applicant.id}&token=${encodeURIComponent(token)}`)
    : null;

  const handleDownload = async () => {
    if (!resumeUrl) return;
    
    setIsDownloading(true);
    
    try {
      const response = await fetch(resumeUrl);
      
      if (!response.ok) {
        throw new Error('Failed to download resume');
      }
      
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('File is empty');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = applicant.resume_filename || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download resume. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="applicant-modal" role="dialog" aria-modal="true" aria-label="Applicant details">
      <div className="applicant-modal__backdrop" onClick={onClose} />
      <div className="applicant-modal__panel">
        <div className="applicant-modal__header">
          <div>
            <p className="applicant-modal__name">{applicant.full_name}</p>
            <span className="applicant-modal__job">{applicant.job_title}</span>
          </div>
          <button type="button" className="applicant-modal__close" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div className="applicant-modal__body">
          <div className="applicant-detail__grid">
            <div className="applicant-detail__item">
              <label>Email</label>
              <p><a href={`mailto:${applicant.email}`}>{applicant.email}</a></p>
            </div>
            <div className="applicant-detail__item">
              <label>Phone</label>
              <p><a href={`tel:${applicant.phone}`}>{applicant.phone || "—"}</a></p>
            </div>
            <div className="applicant-detail__item">
              <label>Experience</label>
              <p>{applicant.experience != null ? `${applicant.experience} year(s)` : "—"}</p>
            </div>
            <div className="applicant-detail__item">
              <label>Applied on</label>
              <p>{formatDate(applicant.applied_at)}</p>
            </div>
          </div>

          {applicant.cover_letter ? (
            <div className="applicant-detail__cover">
              <label>Cover Letter</label>
              <p>{applicant.cover_letter}</p>
            </div>
          ) : null}

          {resumeUrl ? (
            <button 
              className="applicant-detail__resume" 
              onClick={handleDownload}
              type="button"
              disabled={isDownloading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              {isDownloading ? 'Downloading...' : 'Download CV'}
            </button>
          ) : (
            <p className="admin-table__muted" style={{ fontFamily: "Inter, sans-serif", fontSize: 13 }}>
              No CV uploaded.
            </p>
          )}

          <div className="applicant-detail__status-row">
            <label htmlFor="detail-status">Status</label>
            <select
              id="detail-status"
              className="status-select"
              value={applicant.status || "pending"}
              onChange={(e) => onStatusChange(applicant, e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminApplicants() {
  const [isReady, setIsReady] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [usingDemo, setUsingDemo] = useState(false);

  const [search, setSearch] = useState("");
  const [jobFilter, setJobFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selected, setSelected] = useState(null);

  // ✅ استخدام useAdminAuth للتحقق من تسجيل الدخول
  const { isAdmin, isLoading: authLoading } = useAdminAuth();
  const navigate = useNavigate();

  // ✅ التحقق من المصادقة
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      // إذا لم يكن الأدمن مسجلاً دخول، ارجع إلى صفحة تسجيل الدخول
      navigate("/admin");
    }
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setIsReady(true), 30);
    return () => clearTimeout(t);
  }, []);

  const loadApplicants = async () => {
    setIsLoading(true);
    setError("");
    setUsingDemo(false);

    try {
      const response = await fetch(apiUrl("/applications.php"), {
        headers: authHeaders(),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to load applicants.");
      }

      if (data && data.success === true && Array.isArray(data.applications)) {
        setApplicants(data.applications);
      } else if (Array.isArray(data)) {
        setApplicants(data);
      } else {
        setApplicants([]);
      }
    } catch (err) {
      console.log("Failed to load applicants:", err.message);
      setApplicants([]);
      setError("Failed to load applicants. Please try again.");
      setUsingDemo(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // ✅ فقط تحميل البيانات إذا كان الأدمن مسجلاً
    if (isAdmin) {
      loadApplicants();
    }
  }, [isAdmin]);

  const handleStatusChange = async (applicant, nextStatus) => {
    const prevStatus = applicant.status;
    setApplicants((prev) =>
      prev.map((a) => (a.id === applicant.id ? { ...a, status: nextStatus } : a))
    );
    setSelected((cur) => (cur && cur.id === applicant.id ? { ...cur, status: nextStatus } : cur));

    try {
      const response = await fetch(
        apiUrl(`/applications.php?id=${encodeURIComponent(applicant.id)}`),
        {
          method: "PUT",
          headers: authHeaders(),
          body: JSON.stringify({ status: nextStatus }),
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || "Unable to update status.");
      }
    } catch (err) {
      setApplicants((prev) =>
        prev.map((a) => (a.id === applicant.id ? { ...a, status: prevStatus } : a))
      );
      setError(err.message || "Unable to update status.");
    }
  };

  const jobOptions = useMemo(() => {
    const map = new Map();
    applicants.forEach((a) => {
      if (a.job_id && !map.has(a.job_id)) {
        map.set(a.job_id, a.job_title || a.job_id);
      }
    });
    return Array.from(map, ([id, title]) => ({ id, title }));
  }, [applicants]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return applicants.filter((a) => {
      if (jobFilter !== "all" && a.job_id !== jobFilter) return false;
      if (statusFilter !== "all" && (a.status || "pending") !== statusFilter) return false;
      if (!q) return true;
      return (
        (a.full_name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q) ||
        (a.job_title || "").toLowerCase().includes(q)
      );
    });
  }, [applicants, search, jobFilter, statusFilter]);

  const stats = useMemo(() => {
    const total = applicants.length;
    const pending = applicants.filter((a) => (a.status || "pending") === "pending").length;
    const accepted = applicants.filter((a) => a.status === "accepted").length;
    return { total, pending, accepted };
  }, [applicants]);

  // ✅ إذا كان لا يزال يتحقق من المصادقة، عرض شاشة تحميل
  if (authLoading) {
    return (
      <section className="admin-applicants">
        <div className="container admin-applicants__container">
          <div className="admin-empty">Loading...</div>
        </div>
      </section>
    );
  }

  // ✅ إذا لم يكن مسجلاً، لا تعرض أي شيء (useEffect سيعيد التوجيه)
  if (!isAdmin) {
    return null;
  }

  return (
    <section className={`admin-applicants ${isReady ? "admin-applicants--ready" : ""}`}>
      <div className="container admin-applicants__container">
        <div className="admin-applicants__toolbar">
          <div>
            <p className="admin-applicants__eyebrow">Careers Admin</p>
            <h1 className="admin-applicants__title">Job Applicants</h1>
            <p className="admin-applicants__subtitle">
              Review everyone who applied to each opening and update their status.
            </p>
          </div>
        </div>

        {usingDemo && (
          <div className="admin-notice" role="status">
            Showing sample data — connect the <code>/applications.php</code> endpoint to load real applicants.
          </div>
        )}

        <div className="admin-applicants__stats">
          <div className="admin-stat admin-stat--accent">
            <div className="admin-stat__value">{stats.total}</div>
            <div className="admin-stat__label">Total Applicants</div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__value">{stats.pending}</div>
            <div className="admin-stat__label">Pending Review</div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__value">{stats.accepted}</div>
            <div className="admin-stat__label">Accepted</div>
          </div>
        </div>

        <div className="admin-filters">
          <div className="admin-filters__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Search by name, email, or job..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search applicants"
            />
          </div>

          <select
            className="admin-filters__select"
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            aria-label="Filter by job"
          >
            <option value="all">All jobs</option>
            {jobOptions.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title}
              </option>
            ))}
          </select>

          <select
            className="admin-filters__select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="admin-notice" role="alert" style={{ color: "#e6493f", background: "rgba(230,73,63,0.08)", borderColor: "rgba(230,73,63,0.25)" }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="admin-empty">Loading applicants...</div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty">
            {applicants.length === 0
              ? "No applications received yet."
              : "No applicants match your filters."}
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Job</th>
                  <th>Experience</th>
                  <th>Applied</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className="admin-table__name">{a.full_name}</span>
                      <span className="admin-table__sub">{a.email}</span>
                    </td>
                    <td>
                      <span className="admin-table__badge">{a.job_title}</span>
                    </td>
                    <td className="admin-table__muted">
                      {a.experience != null ? `${a.experience} yr` : "—"}
                    </td>
                    <td className="admin-table__muted">{formatDate(a.applied_at)}</td>
                    <td>
                      <select
                        className="status-select"
                        value={a.status || "pending"}
                        onChange={(e) => handleStatusChange(a, e.target.value)}
                        aria-label={`Status for ${a.full_name}`}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div className="admin-table__actions">
                        <button
                          type="button"
                          className="admin-icon-btn"
                          aria-label={`View ${a.full_name}`}
                          onClick={() => setSelected(a)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="admin-cards">
              {filtered.map((a) => (
                <div className="admin-card" key={a.id}>
                  <div className="admin-card__top">
                    <div>
                      <p className="admin-card__name">{a.full_name}</p>
                      <span className="admin-card__job">{a.job_title}</span>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                  <div className="admin-card__meta">
                    <span>{a.email}</span>
                    <span>{a.phone || "—"}</span>
                    <span>
                      <strong>{a.experience != null ? `${a.experience} yr` : "—"}</strong> experience &middot; {formatDate(a.applied_at)}
                    </span>
                  </div>
                  <div className="admin-card__actions">
                    <button
                      type="button"
                      className="admin-icon-btn"
                      onClick={() => setSelected(a)}
                      aria-label={`View ${a.full_name}`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {selected && (
        <ApplicantModal
          applicant={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </section>
  );
}